import { useEffect, useState } from "react";
import {
  CreditCard, ShieldCheck, AlertTriangle, Save, HelpCircle, Copy, ShieldOff,
  Percent, ListChecks, CheckCircle2, XCircle, Euro, PhoneCall,
} from "lucide-react";
import api, { eur, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const Lbl = ({ children }) => <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>;

const ANALYTICS_PERIODS = [
  { key: "7", label: "7 jours" },
  { key: "30", label: "30 jours" },
  { key: "90", label: "90 jours" },
  { key: "custom", label: "Personnalisé" },
];

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

const StatTile = ({ icon: Icon, iconClassName, label, value, testid }) => (
  <div data-testid={testid} className="bg-white rounded-sm border border-stone p-5">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.16em] text-ash">{label}</p>
      <Icon className={`h-5 w-5 ${iconClassName || "text-copper"}`} strokeWidth={1.5} />
    </div>
    <p className="font-serif text-3xl text-ink mt-3">{value}</p>
  </div>
);

function PaymentAnalytics() {
  const [preset, setPreset] = useState("30");
  const [customStart, setCustomStart] = useState(isoDaysAgo(30));
  const [customEnd, setCustomEnd] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const params = preset === "custom"
    ? { start: customStart, end: customEnd }
    : { period: Number(preset) };

  useEffect(() => {
    setLoading(true);
    setError(false);
    api.get("/superadmin/stripe/analytics", { params })
      .then(({ data }) => setData(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, customStart, customEnd]);

  return (
    <div className="bg-white border border-stone rounded-sm p-6 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Percent className="h-6 w-6 text-copper" strokeWidth={1.5} />
          <h2 className="font-serif text-2xl text-ink">Taux de réussite des paiements</h2>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex bg-sand border border-stone rounded-sm overflow-hidden">
            {ANALYTICS_PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                data-testid={`stripe-analytics-period-${p.key}`}
                onClick={() => setPreset(p.key)}
                className={`px-3.5 py-2 text-xs uppercase tracking-[0.1em] transition-colors ${
                  preset === p.key ? "bg-ink text-cream" : "text-graphite hover:bg-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex items-center gap-2" data-testid="stripe-analytics-custom-inputs">
              <input
                type="date" data-testid="stripe-analytics-start-input" value={customStart} max={customEnd}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border border-stone bg-white text-sm px-2.5 py-1.5 rounded-sm"
              />
              <span className="text-ash text-sm">→</span>
              <input
                type="date" data-testid="stripe-analytics-end-input" value={customEnd} min={customStart} max={todayIso()}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border border-stone bg-white text-sm px-2.5 py-1.5 rounded-sm"
              />
            </div>
          )}
        </div>
      </div>

      <p className="text-xs text-ash">
        Calculé en direct depuis l&apos;API Stripe (tous les paiements du compte actif, pas seulement les commandes de ce site).
      </p>

      {error && (
        <p className="text-sm text-destructive">
          Impossible de récupérer les statistiques Stripe pour cette période. Vérifiez la clé API configurée.
        </p>
      )}

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 bg-sand animate-pulse rounded-sm" />)}
        </div>
      ) : data && !error ? (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              testid="stripe-stat-success-rate"
              icon={Percent}
              label="Taux de réussite"
              value={data.success_rate === null ? "—" : `${data.success_rate}%`}
            />
            <StatTile testid="stripe-stat-attempts" icon={ListChecks} label="Tentatives" value={data.total_attempts} />
            <StatTile testid="stripe-stat-succeeded" icon={CheckCircle2} iconClassName="text-green-600" label="Réussis" value={data.succeeded} />
            <StatTile testid="stripe-stat-failed" icon={XCircle} iconClassName="text-red-600" label="Échoués" value={data.failed} />
          </div>
          <div className="flex items-center gap-2 text-sm text-graphite">
            <Euro className="h-4 w-4 text-copper" />
            Volume encaissé sur la période : <strong className="text-ink font-mono">{eur(data.volume_succeeded)}</strong>
          </div>
          {data.total_attempts === 0 && (
            <p className="text-xs text-ash">Aucun paiement enregistré sur cette période pour ce mode ({data.currency?.toUpperCase()}).</p>
          )}
          {data.truncated && (
            <p className="text-xs text-ash">Estimation basée sur les 1000 paiements les plus récents de la période (volume trop élevé pour tout charger).</p>
          )}
        </>
      ) : null}
    </div>
  );
}

const KEY_PREFIXES = { test: ["sk_test_", "rk_test_"], live: ["sk_live_", "rk_live_"] };

function keyMatchesEnv(key, env) {
  if (!key) return true; // champ vide : rien à valider ici, le serveur tranchera
  return KEY_PREFIXES[env].some((p) => key.startsWith(p));
}

function copyToClipboard(value, label) {
  if (!value) return;
  navigator.clipboard?.writeText(value);
  toast.success(label || "Copié");
}

function HelpButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="stripe-webhook-help-btn"
      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.1em] text-copper hover:text-ink transition-colors"
    >
      <HelpCircle className="h-4 w-4" /> {label || "Aide"}
    </button>
  );
}

function WebhookGuideModal({ open, onOpenChange, webhookUrl }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream max-w-lg max-h-[85vh] overflow-y-auto" data-testid="stripe-webhook-guide">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ink">Configurer le secret webhook</DialogTitle>
          <DialogDescription className="text-graphite text-sm">
            Étapes pour relier ton compte Stripe à cet endpoint et récupérer le secret de signature.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm text-graphite space-y-3">
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Dans le <strong className="text-ink">Dashboard Stripe</strong>, vérifie le toggle Test/Live en haut à droite — il doit correspondre au mode choisi sur cette page.
            </li>
            <li>
              Développeurs → Webhooks → <em>Ajouter un endpoint</em>. Colle l&apos;URL suivante :
              <div className="flex items-center gap-2 mt-1.5">
                <code className="flex-1 bg-white border border-stone rounded-sm px-2 py-1.5 text-xs font-mono break-all">
                  {webhookUrl || "URL indisponible (PUBLIC_BACKEND_URL non configurée)"}
                </code>
                {webhookUrl && (
                  <button type="button" onClick={() => copyToClipboard(webhookUrl, "URL copiée")} className="shrink-0 p-1.5 border border-stone rounded-sm hover:bg-white" title="Copier l'URL">
                    <Copy className="h-3.5 w-3.5 text-graphite" />
                  </button>
                )}
              </div>
            </li>
            <li>Événements à écouter : sélectionne au minimum <code className="font-mono text-xs">checkout.session.completed</code>.</li>
            <li>Valide, puis clique sur <em>Révéler</em> à côté de « Signing secret » sur la page de l&apos;endpoint créé — tu obtiens une valeur <code className="font-mono text-xs">whsec_...</code>.</li>
            <li>Colle ce secret dans le champ <strong className="text-ink">Test</strong> ou <strong className="text-ink">Live</strong> selon le mode où tu as créé l&apos;endpoint, puis Enregistrer.</li>
          </ol>
          <p className="text-xs text-ash">
            Un endpoint Stripe Test et un endpoint Stripe Live sont deux configurations séparées, avec chacune leur propre secret —
            répète l&apos;opération pour l&apos;autre mode si tu utilises les deux.
          </p>
          <p className="text-xs text-ash">
            Pour vérifier que ça fonctionne : fais un paiement de test, puis regarde dans Dashboard Stripe → Webhooks → ton endpoint →
            onglet « Tentatives récentes ». Un <strong>200</strong> confirme que tout est en ordre ; un <strong>400</strong> signifie que le
            secret collé ne correspond pas à cet endpoint, ou que le mode Test/Live ne correspond pas.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SuperStripe() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [mode, setMode] = useState("test");
  const [testKey, setTestKey] = useState("");
  const [liveKey, setLiveKey] = useState("");
  const [webhookSecretTest, setWebhookSecretTest] = useState("");
  const [webhookSecretLive, setWebhookSecretLive] = useState("");
  const [verifyDisabled, setVerifyDisabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [siteConfig, setSiteConfig] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);

  const load = () => Promise.all([
    api.get("/superadmin/stripe"),
    api.get("/site-config"),
  ]).then(([stripeRes, siteRes]) => {
    setCfg(stripeRes.data);
    setMode(stripeRes.data.mode);
    setVerifyDisabled(!!stripeRes.data.webhook_verify_disabled);
    setSiteConfig(siteRes.data);
  });
  useEffect(() => { load(); }, []);

  if (user?.role !== "superadmin") return <p className="text-graphite">Accès réservé au super administrateur.</p>;

  const togglePayment = async (enabled) => {
    if (!enabled && !window.confirm(
      "Désactiver le paiement en ligne masque le bouton \"Payer\" sur tout le site : les clients verront un bouton \"Contacter\" à la place. Continuer ?"
    )) return;
    setSavingPayment(true);
    try {
      const { data } = await api.put("/superadmin/site-config", { payment_enabled: enabled });
      setSiteConfig(data);
      toast.success(enabled ? "Paiement en ligne activé." : "Paiement en ligne désactivé.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPayment(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (testKey.trim() && !keyMatchesEnv(testKey.trim(), "test")) {
      toast.error("La clé Test doit commencer par sk_test_ (ou rk_test_) — as-tu collé la clé Live par erreur ?");
      return;
    }
    if (liveKey.trim() && !keyMatchesEnv(liveKey.trim(), "live")) {
      toast.error("La clé Live doit commencer par sk_live_ (ou rk_live_) — as-tu collé la clé Test par erreur ?");
      return;
    }
    if (verifyDisabled && !window.confirm(
      "Désactiver la vérification de signature webhook rend l'endpoint /api/webhook/stripe utilisable par n'importe qui pour simuler un paiement confirmé. À n'utiliser que temporairement pour du débogage. Continuer ?"
    )) return;
    setSaving(true);
    try {
      const payload = { mode, webhook_verify_disabled: verifyDisabled };
      if (testKey.trim()) payload.test_key = testKey.trim();
      if (liveKey.trim()) payload.live_key = liveKey.trim();
      if (webhookSecretTest.trim()) payload.webhook_secret_test = webhookSecretTest.trim();
      if (webhookSecretLive.trim()) payload.webhook_secret_live = webhookSecretLive.trim();
      const { data } = await api.put("/superadmin/stripe", payload);
      setCfg(data);
      setTestKey(""); setLiveKey("");
      setWebhookSecretTest(""); setWebhookSecretLive("");
      toast.success("Configuration Stripe enregistrée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="super-stripe" className="max-w-4xl">
      <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Super Admin</p>
      <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight mb-8">Paiement Stripe</h1>

      <div className="bg-white border border-stone rounded-sm p-5 mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <PhoneCall className="h-5 w-5 text-copper shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <h2 className="font-serif text-xl text-ink">Paiement en ligne</h2>
            <p className="text-sm text-graphite mt-1 max-w-xl">
              Si désactivé, le bouton « Payer » du tunnel de commande est remplacé par un bouton
              « Contacter » : le client envoie ses coordonnées et les produits de son panier au lieu
              de payer sur Stripe.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0" data-testid="payment-toggle-wrap">
          <span className="text-xs uppercase tracking-[0.15em] text-ash">
            {siteConfig?.payment_enabled !== false ? "Activé" : "Désactivé"}
          </span>
          <Switch
            data-testid="payment-enabled-toggle"
            checked={siteConfig?.payment_enabled !== false}
            disabled={!siteConfig || savingPayment}
            onCheckedChange={togglePayment}
            className="data-[state=checked]:bg-gold"
          />
        </div>
      </div>

      {!cfg ? (
        <div className="h-64 bg-white border border-stone animate-pulse rounded-sm max-w-2xl" />
      ) : (
        <>
          <PaymentAnalytics />
          <form onSubmit={save} className="bg-white border border-stone rounded-sm p-6 space-y-6 max-w-2xl mt-6">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6 text-copper" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl text-ink">Clés API & mode</h2>
          </div>

          {cfg.platform_test_active && (
            <div className="flex items-start gap-3 bg-sand rounded-sm p-4 text-sm text-graphite">
              <ShieldCheck className="h-5 w-5 text-copper shrink-0 mt-0.5" />
              <p>Actuellement, le paiement utilise la <strong className="text-ink">clé de test de la plateforme</strong>. Saisissez vos propres clés pour passer sur votre compte Stripe.</p>
            </div>
          )}

          <div>
            <Lbl>Mode de paiement</Lbl>
            <div className="flex gap-2 mt-2">
              <button type="button" data-testid="stripe-mode-test" onClick={() => setMode("test")} className={`flex-1 py-3 text-xs uppercase tracking-[0.12em] border ${mode === "test" ? "bg-ink text-cream border-ink" : "border-stone text-graphite"}`}>Test</button>
              <button type="button" data-testid="stripe-mode-live" onClick={() => setMode("live")} className={`flex-1 py-3 text-xs uppercase tracking-[0.12em] border ${mode === "live" ? "bg-ink text-cream border-ink" : "border-stone text-graphite"}`}>Production (Live)</button>
            </div>
            {mode === "live" && (
              <p className="flex items-center gap-2 text-xs text-copper mt-2"><AlertTriangle className="h-4 w-4" /> En mode Live, les paiements sont réels.</p>
            )}
          </div>

          {mode === "test" ? (
            <div>
              <Lbl>Clé secrète Test (sk_test_…)</Lbl>
              <Input data-testid="stripe-test-key" type="password" value={testKey} onChange={(e) => setTestKey(e.target.value)} placeholder={cfg.test_key_set ? `Configurée : ${cfg.test_key_masked}` : "sk_test_..."} className="mt-1.5 h-12 rounded-sm border-stone bg-white font-mono text-sm" />
              <p className="text-xs text-ash mt-1">{cfg.test_key_set ? "Laissez vide pour conserver la clé actuelle." : "Non configurée."}</p>
            </div>
          ) : (
            <div>
              <Lbl>Clé secrète Live (sk_live_…)</Lbl>
              <Input data-testid="stripe-live-key" type="password" value={liveKey} onChange={(e) => setLiveKey(e.target.value)} placeholder={cfg.live_key_set ? `Configurée : ${cfg.live_key_masked}` : "sk_live_..."} className="mt-1.5 h-12 rounded-sm border-stone bg-white font-mono text-sm" />
              <p className="text-xs text-ash mt-1">{cfg.live_key_set ? "Laissez vide pour conserver la clé actuelle." : "Non configurée."}</p>
            </div>
          )}

          <div className="border-t border-stone pt-6 space-y-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-serif text-2xl text-ink">Secrets webhook</h2>
                <p className="text-xs text-ash mt-1">
                  Signature attendue sur l&apos;endpoint <code className="font-mono">/api/webhook/stripe</code>. Sans secret configuré ici,
                  le webhook retombe sur la variable d&apos;environnement globale{" "}
                  <code className="font-mono">STRIPE_WEBHOOK_SECRET</code>{cfg.webhook_secret_env_set ? " (actuellement définie)" : " (non définie)"}.
                </p>
              </div>
              <HelpButton onClick={() => setShowGuide(true)} />
            </div>

            {mode === "test" ? (
              <div>
                <Lbl>Secret webhook Test (whsec_…)</Lbl>
                <Input data-testid="stripe-webhook-test" type="password" value={webhookSecretTest} onChange={(e) => setWebhookSecretTest(e.target.value)} placeholder={cfg.webhook_secret_test_set ? `Configuré : ${cfg.webhook_secret_test_masked}` : "whsec_..."} className="mt-1.5 h-12 rounded-sm border-stone bg-white font-mono text-sm" />
                <p className="text-xs text-ash mt-1">{cfg.webhook_secret_test_set ? "Laissez vide pour conserver le secret actuel." : "Non configuré."}</p>
              </div>
            ) : (
              <div>
                <Lbl>Secret webhook Live (whsec_…)</Lbl>
                <Input data-testid="stripe-webhook-live" type="password" value={webhookSecretLive} onChange={(e) => setWebhookSecretLive(e.target.value)} placeholder={cfg.webhook_secret_live_set ? `Configuré : ${cfg.webhook_secret_live_masked}` : "whsec_..."} className="mt-1.5 h-12 rounded-sm border-stone bg-white font-mono text-sm" />
                <p className="text-xs text-ash mt-1">{cfg.webhook_secret_live_set ? "Laissez vide pour conserver le secret actuel." : "Non configuré."}</p>
              </div>
            )}

            <div className={`rounded-sm p-4 border ${verifyDisabled ? "border-red-300 bg-red-50" : "border-stone bg-sand/40"}`}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="stripe-webhook-verify-disabled"
                  checked={verifyDisabled}
                  onChange={(e) => setVerifyDisabled(e.target.checked)}
                  className="accent-red-600 mt-0.5"
                />
                <span>
                  <span className={`flex items-center gap-1.5 font-medium ${verifyDisabled ? "text-red-700" : "text-ink"}`}>
                    <ShieldOff className="h-4 w-4" /> Désactiver la vérification de signature webhook
                  </span>
                  <span className="block text-xs text-graphite mt-1">
                    Réservé au débogage ponctuel (webhook qui ne se déclenche pas malgré une config a priori correcte).
                    Tant que c&apos;est actif, <strong>n&apos;importe qui connaissant l&apos;URL de l&apos;endpoint peut simuler un
                    paiement confirmé</strong> sans passer par Stripe. À réactiver dès le débogage terminé.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <button data-testid="save-stripe-btn" type="submit" disabled={saving} className="flex items-center gap-2 bg-ink text-cream px-8 py-4 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
            <Save className="h-4 w-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
          </form>

          <WebhookGuideModal open={showGuide} onOpenChange={setShowGuide} webhookUrl={cfg.webhook_url} />
        </>
      )}
    </div>
  );
}
